import React, { useState, useCallback, useRef, useEffect, ReactNode } from "react";
import { TreeSelect } from "primereact/treeselect";
import { Button } from "primereact/button";
import MultiSearch from "../../../../Components/SearchInputs/MultiSearch/MultiSearch";
import { ChannelManagerOption, CMSOptions } from "../../Objects/multiSelectObjects";
import ExcelExport from "../../../../Components/Buttons/ExcelExport";
import { RowData } from "../../Interfaces/interfaces";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import "./auditFilters.css"; // Import the CSS file
import { MultiSelect } from "primereact/multiselect";
import { Image } from "primereact/image";
import Confetti from 'react-confetti'
import { buildIssueExportName } from "../Utils/exportFileName";


interface AuditFiltersProps {
  onFilterChange: (filters: any) => void;
  rows: any[];
  issueSelction: any; // Expecting a tree-like structure now
  exportColumns: any["columnsToExport"];
  prepareExport?: (data: any[]) => Promise<void>;
  filteredRows: any[];
  exportName: string;
  disabled: boolean;
  myIssuesOnly: boolean;
  channelManagerOptions:ChannelManagerOption[];
  footerAction?: ReactNode;
  forcedLegalEntityType?: string | string[];
  onSearch?: (filters: any) => void;
  onReset?: (filters: any) => void;
  initialVideoId?: string | string[];
  initialCMS?: string | string[];
  initialChannelNames?: string | string[];
  initialChannelName?: string | string[];
  initialChannelId?: string | string[];
  initialChannelManagers?: string | string[];
  initialIssueKeys?: string | string[];
  initialPrivacyStatus?: string | string[];
  initialContentFormat?: string | string[];
  initialLegalEntity?: string | string[];
  initialLegalEntityType?: string | string[];
  initialMyIssuesOnly?: boolean;
}

const customToastTemplate = (): ReactNode => {
  return (
    <div className="flex align-items-center">
      <Image
        src="/noIssues.gif" // Corrected path
        alt="Celebration"
        width="100"
        height="80"
        style={{ paddingTop: "12px" }}
        className="mr-2"
      />

      <div>
        <div className="font-bold">No Issues! 🥳</div>
        <div>Well done, you have no issues related to your account 🎉</div>
      </div>
    </div>
  );
};

export const getPrivacy = (row: RowData): string => {
  const status = String(row.videoPrivacyStatus ?? "").toLowerCase();
  if (status === "public") {
    return "Public";
  } else if (status === "private" && row.PublishDate === "Missing") {
    return "Private";
  } else if (status === "private" && row.PublishDate !== "Missing") {
    return "Scheduled";
  } else {
    return "N/A";
  }
};

export const getCustomIDStatus = (row: RowData) => {
  // Use the backend's customidStatus value directly
  // The backend validates against the BigQuery MasterCatalog (single source of truth)
  const status = (row as any).customidStatus;
  
  if (status === "Whitelisted") return "Whitelisted";
  if (status === "Correct") return "Correct";
  if (status === "N/A") return "N/A";
  if (status === "Incorrect") return "Incorrect";
  
  // Fallback for any unexpected/missing value
  return "Incorrect";
};

const AuditFilters: React.FC<AuditFiltersProps> = ({
  onFilterChange,
  rows,
  issueSelction,
  exportColumns,
  prepareExport,
  filteredRows,
  disabled,
  exportName,
  myIssuesOnly,
  channelManagerOptions,
  footerAction,
  forcedLegalEntityType,
  onSearch,
  onReset,
  initialVideoId,
  initialCMS,
  initialChannelNames,
  initialLegalEntity,
  initialChannelName,
  initialChannelId,
  initialChannelManagers,
  initialIssueKeys,
  initialPrivacyStatus,
  initialContentFormat,
  initialLegalEntityType,
  initialMyIssuesOnly,
}) => {
  
  const [selectedVideoID, setSelectedVideoID] = useState<string[]>([]);
  const [selectedCMS, setSelectedCMS] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<any>(null); // Changed to any for TreeSelect
  const [selectedChannelID, setSelectedChannelID] = useState<string[]>([]);
  const [selectedChannelManagers, setSelectedChannelManagers] = useState<
    string[]
  >([]);
  const [selectedFilters, setSelectedFilters] = useState<any>(null); // State holds the TreeSelect's value object
  const [selectedPrivacyStatus, setSelectedPrivacyStatus] = useState<string[]>(
    []
  );
  const [selectedContentFormat, setSelectedContentFormat] = useState<string[]>(
    []
  );
  const [selectedLegalEntity, setSelectedLegalEntity] = useState<string[]>([]);
  const [selectedLegalEntityType, setSelectedLegalEntityType] = useState<string[]>([]);
  const [showConfetti, setShowConfetti] = useState(false); // New state for confetti
  const [channelTree, setChannelTree] = useState<any[]>([]); // Tree structure for channels
  const [expandedKeys, setExpandedKeys] = useState<any>({ "all-channels": true }); // Keep "All Channels" expanded by default

  const toast = useRef<Toast>(null); // Create a ref for the Toast
  const initialFiltersApplied = useRef(false);
  const lastEmittedFiltersRef = useRef<string>("");
  console.log("AuditFilters received channelManagerOptions:", channelManagerOptions);

  const normalizeInitialArray = (value?: string | string[]) =>
    value ? (Array.isArray(value) ? value : [value]) : [];

  const enforcedLegalEntityTypes = normalizeInitialArray(forcedLegalEntityType);

  const buildTreeSelectionValue = (selectedKeys: string[], tree: any[]) => {
    const selectionValue: Record<string, { checked: boolean; partialChecked: boolean }> =
      {};
    const parentMap = new Map<string, string[]>();

    const buildParentMap = (nodes: any[], parents: string[] = []) => {
      nodes.forEach((node) => {
        const key = node?.key ? String(node.key) : "";
        if (key) {
          parentMap.set(key, parents);
        }
        if (node?.children?.length) {
          buildParentMap(node.children, key ? [...parents, key] : parents);
        }
      });
    };

    buildParentMap(tree);

    selectedKeys.forEach((key) => {
      if (!key || !parentMap.has(key)) return;
      selectionValue[key] = { checked: true, partialChecked: false };
      const parents = parentMap.get(key) || [];
      parents.forEach((parentKey) => {
        if (!selectionValue[parentKey]) {
          selectionValue[parentKey] = { checked: false, partialChecked: true };
        } else if (selectionValue[parentKey].checked) {
          selectionValue[parentKey].checked = false;
          selectionValue[parentKey].partialChecked = true;
        }
      });
    });

    return selectionValue;
  };

  const buildFiltersPayload = useCallback(
    (overrideMyIssuesOnly?: boolean) => ({
        selectedVideoID,
        selectedCMS,
        selectedChannels,
        selectedChannelID,
        selectedChannelManagers,
        selectedFilters, // Pass the entire selectedFilters object
        selectedPrivacyStatus,
        selectedContentFormat,
        selectedLegalEntity,
        selectedLegalEntityType,
        myIssuesOnly:
          typeof overrideMyIssuesOnly === "boolean"
            ? overrideMyIssuesOnly
            : myIssuesOnly,
      }),
    [
      selectedVideoID,
      selectedCMS,
      selectedChannels,
      selectedChannelID,
      selectedChannelManagers,
      selectedFilters,
      selectedPrivacyStatus,
      selectedContentFormat,
      selectedLegalEntity,
      selectedLegalEntityType,
      myIssuesOnly,
    ]
  );

  const emitFilterChange = useCallback(
    (overrideMyIssuesOnly?: boolean) => {
      const payload = buildFiltersPayload(overrideMyIssuesOnly);
      const payloadKey = JSON.stringify(payload);
      if (payloadKey === lastEmittedFiltersRef.current) {
        return;
      }

      lastEmittedFiltersRef.current = payloadKey;
      onFilterChange(payload);
    },
    [buildFiltersPayload, onFilterChange]
  );

  // Build channel tree structure from rows
  useEffect(() => {
    if (rows && rows.length > 0) {
      const cmsChannelMap = new Map<string, Set<string>>();
      
      // Group channels by CMS
      rows.forEach((row) => {
        const cms = row.CMS || "Unknown CMS";
        const channel = row.Channel_Name;
        
        if (channel) {
          if (!cmsChannelMap.has(cms)) {
            cmsChannelMap.set(cms, new Set());
          }
          cmsChannelMap.get(cms)?.add(channel);
        }
      });

      // Build tree structure
      const tree: any[] = [];
      
      // Add "All Channels" parent node
      const allChannelsChildren: any[] = [];
      
      // Sort CMS alphabetically
      const sortedCMS = Array.from(cmsChannelMap.keys()).sort();
      
      sortedCMS.forEach((cms) => {
        const channels = Array.from(cmsChannelMap.get(cms) || []).sort();
        const cmsNode = {
          key: `cms___${cms}`,
          label: cms,
          children: channels.map((channel) => ({
            key: `channel___${channel}`,
            label: channel,
            data: channel, // Store the actual channel name
          })),
        };
        allChannelsChildren.push(cmsNode);
      });

      tree.push({
        key: "all-channels",
        label: "All Channels",
        children: allChannelsChildren,
      });

      setChannelTree(tree);
      
      // Ensure "All Channels" stays expanded
      setExpandedKeys((prevKeys: any) => ({
        ...prevKeys,
        "all-channels": true
      }));
    }
  }, [rows]);

  const privacyOptions = [
    { label: "Public", value: "Public" },
    { label: "Private", value: "Private" },
    { label: "Scheduled", value: "Scheduled" },
  ];
  const contentFormatOptions = [
    { label: "Video", value: "Video" },
    { label: "Short", value: "Short" },
    { label: "Livestream", value: "Livestream" },
    { label: "Unspecified", value: "Unspecified" },
  ];

  // Generate LegalEntity options from rows data
  const legalEntityOptions = Array.from(
    new Set(rows.map((item: any) => item.LegalEntity).filter(Boolean))
  ).map((value) => ({ label: String(value), value: String(value) }));

  // Generate LegalEntityType options from rows data
  const legalEntityTypeOptions = Array.from(
    new Set(rows.map((item: any) => item.LegalEntityType).filter(Boolean))
  ).map((value) => ({ label: String(value), value: String(value) }));

  const handleFilterChange = useCallback(() => {
    emitFilterChange();
  }, [emitFilterChange]);

  const handleMyIssuesToggle = (e:any) => {
    onFilterChange({...e, myIssuesOnly: e.value});
  };

  useEffect(() => {
    emitFilterChange();
  }, [myIssuesOnly, emitFilterChange]);

  useEffect(() => {
    if (enforcedLegalEntityTypes.length > 0) {
      setSelectedLegalEntityType(enforcedLegalEntityTypes);
    }
  }, [enforcedLegalEntityTypes]);

  const resetFilters = () => {
    setSelectedVideoID([]);
    setSelectedCMS([]);
    setSelectedChannels(null); // Reset TreeSelect value to null
    setSelectedChannelID([]);
    setSelectedChannelManagers([]);
    setSelectedFilters(null); // Reset TreeSelect value to null
    setSelectedPrivacyStatus([]);
    setSelectedContentFormat([]);
    setSelectedLegalEntity([]);
    setSelectedLegalEntityType(
      enforcedLegalEntityTypes.length > 0 ? enforcedLegalEntityTypes : []
    );
    const resetPayload = {
      selectedVideoID: [],
      selectedCMS: [],
      selectedChannels: null,
      selectedChannelID: [],
      selectedChannelManagers: [],
      selectedFilters: null,
      selectedPrivacyStatus: [],
      selectedContentFormat: [],
      selectedLegalEntity: [],
      selectedLegalEntityType:
        enforcedLegalEntityTypes.length > 0 ? enforcedLegalEntityTypes : [],
      myIssuesOnly,
    };
    if (onReset) {
      onReset(resetPayload);
      return;
    }
    emitFilterChange();
  };

  useEffect(() => {
    if (initialFiltersApplied.current) return;

    const initialVideoIds = normalizeInitialArray(initialVideoId);
    const initialCMSValues = normalizeInitialArray(initialCMS);
    const initialChannelNamesList = normalizeInitialArray(
      initialChannelNames ?? initialChannelName
    );
    const initialChannelIds = normalizeInitialArray(initialChannelId);
    const initialChannelManagerNames = normalizeInitialArray(initialChannelManagers);
    const initialIssueKeysList = normalizeInitialArray(initialIssueKeys);
    const initialPrivacyStatuses = normalizeInitialArray(initialPrivacyStatus);
    const initialContentFormats = normalizeInitialArray(initialContentFormat);
    const initialLegalEntities = normalizeInitialArray(initialLegalEntity);
    const initialLegalEntityTypes = normalizeInitialArray(initialLegalEntityType);
    const legalEntityTypesToApply =
      enforcedLegalEntityTypes.length > 0
        ? enforcedLegalEntityTypes
        : initialLegalEntityTypes;

    const hasInitialFilters =
      initialVideoIds.length > 0 ||
      initialCMSValues.length > 0 ||
      initialChannelNamesList.length > 0 ||
      initialChannelIds.length > 0 ||
      initialChannelManagerNames.length > 0 ||
      initialIssueKeysList.length > 0 ||
      initialPrivacyStatuses.length > 0 ||
      initialContentFormats.length > 0 ||
      initialLegalEntities.length > 0 ||
      legalEntityTypesToApply.length > 0 ||
      typeof initialMyIssuesOnly === "boolean";

    if (!hasInitialFilters) return;

    const needsChannelTree = initialChannelNamesList.length > 0;
    const channelTreeReady = !needsChannelTree || (channelTree.length > 0 && rows && rows.length > 0);
    if (!channelTreeReady) return;

    if (initialVideoIds.length > 0) {
      setSelectedVideoID(initialVideoIds);
    }

    if (initialCMSValues.length > 0) {
      setSelectedCMS(initialCMSValues);
    }

    if (initialChannelIds.length > 0) {
      setSelectedChannelID(initialChannelIds);
    }

    if (initialChannelManagerNames.length > 0) {
      setSelectedChannelManagers(initialChannelManagerNames);
    }

    if (initialPrivacyStatuses.length > 0) {
      setSelectedPrivacyStatus(initialPrivacyStatuses);
    }

    if (initialContentFormats.length > 0) {
      setSelectedContentFormat(initialContentFormats);
    }

    if (initialLegalEntities.length > 0) {
      setSelectedLegalEntity(initialLegalEntities);
    }

    if (legalEntityTypesToApply.length > 0) {
      setSelectedLegalEntityType(legalEntityTypesToApply);
    }

    if (initialIssueKeysList.length > 0) {
      const selectionValue = buildTreeSelectionValue(
        initialIssueKeysList,
        issueSelction || []
      );
      setSelectedFilters(selectionValue);
    }

    if (initialChannelNamesList.length > 0 && rows && rows.length > 0) {
      const channelKeySet = new Set<string>();
      initialChannelNamesList.forEach((channelName) => {
        const normalized = channelName.trim().toLowerCase();
        const matchingChannel = rows.find((row: any) => {
          const rowChannelName = (row.Channel_Name || "").toString().trim();
          return rowChannelName.toLowerCase() === normalized;
        });
        const channelValue =
          matchingChannel?.Channel_Name?.toString().trim() || channelName.trim();
        if (channelValue) {
          channelKeySet.add(`channel___${channelValue}`);
        }
      });

      const channelKeys = Array.from(channelKeySet);
      if (channelKeys.length > 0 && channelTree.length > 0) {
        const selectionValue = buildTreeSelectionValue(channelKeys, channelTree);
        setSelectedChannels(selectionValue);
        const cmsKeys = Object.keys(selectionValue).filter((key) =>
          key.startsWith("cms___")
        );
        setExpandedKeys((prev: any) => ({
          ...(prev || {}),
          "all-channels": true,
          ...cmsKeys.reduce<Record<string, boolean>>((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        }));
      }
    }

    initialFiltersApplied.current = true;
    setTimeout(() => {
      emitFilterChange(initialMyIssuesOnly);
    }, 0);
  }, [
    rows,
    channelTree,
    issueSelction,
    initialVideoId,
    initialCMS,
    initialChannelNames,
    initialChannelName,
    initialChannelId,
    initialChannelManagers,
    initialIssueKeys,
    initialPrivacyStatus,
    initialContentFormat,
    initialLegalEntity,
    initialLegalEntityType,
    initialMyIssuesOnly,
    emitFilterChange,
  ]);

  return (
    <>
      <div id="audit-filters-container" className="grid-container" style={{ marginTop: "-1rem" }}>
        {" "}
        {/* grid container */}
        <div className="filter-item">
          <MultiSearch
            selectedItems={selectedVideoID}
            value={(e) => setSelectedVideoID(e)}
            label={"Video ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            CMS
          </text>
          <MultiSelect
            value={selectedCMS}
            options={CMSOptions}
            onChange={(e) => setSelectedCMS(e.value)}
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Channel
          </text>
          <div id="channel-treeselect">
            <TreeSelect
              value={selectedChannels}
              options={channelTree}
              onChange={(e) => {
                setSelectedChannels(e.value);
              }}
              expandedKeys={expandedKeys}
              onToggle={(e) => setExpandedKeys(e.value)}
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              selectionMode="checkbox"
              display="chip"
            />
          </div>
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={selectedChannelID}
            value={(e) => setSelectedChannelID(e)}
            label={"Channel ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Channel Manager
          </text>
          <MultiSelect
            value={selectedChannelManagers}
            options={channelManagerOptions}
            onChange={(e) => setSelectedChannelManagers(e.value)}
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Content Format
          </text>
          <div className="flex-1">
            <MultiSelect
              value={selectedContentFormat}
              options={contentFormatOptions}
              onChange={(e) => setSelectedContentFormat(e.value)}
              optionLabel="label"
              maxSelectedLabels={1}
              style={{ width: "90%", borderRadius: "5px" }}
            />
          </div>
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Privacy Status
          </text>
          <div className="flex-1">
            <MultiSelect
              value={selectedPrivacyStatus}
              options={privacyOptions}
              onChange={(e) => setSelectedPrivacyStatus(e.value)}
              optionLabel="label"
              maxSelectedLabels={1}
              style={{ width: "90%", borderRadius: "5px" }}
            />
          </div>
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity
          </text>
          <div className="flex-1">
            <MultiSelect
              value={selectedLegalEntity}
              options={legalEntityOptions}
              onChange={(e) => setSelectedLegalEntity(e.value)}
              optionLabel="label"
              maxSelectedLabels={1}
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              showClear
            />
          </div>
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity Type
          </text>
          <div className="flex-1">
            <MultiSelect
              value={selectedLegalEntityType}
              options={legalEntityTypeOptions}
              onChange={(e) => setSelectedLegalEntityType(e.value)}
              optionLabel="label"
              maxSelectedLabels={1}
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              showClear
              disabled={enforcedLegalEntityTypes.length > 0}
            />
          </div>
        </div>
       
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>Select Issue</text>
          <div id="issue-treeselect">
            <TreeSelect
              value={selectedFilters}
              options={issueSelction}
              onChange={(e) => setSelectedFilters(e.value)}
              style={{ width: "90%", borderRadius: "5px" }}
              filter
              selectionMode="checkbox" // Allow multiple selections in the tree
              display="chip" // Display selected items as chips
            />
          </div>
        </div>
        </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Search"
              icon="pi pi-search"
              onClick={() => {
                if (onSearch) {
                  onSearch(buildFiltersPayload());
                  return;
                }
                handleFilterChange();
              }}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={resetFilters}
            />
          </div>
          <div
        className="your-issues"
        onClick={() => {
          if (disabled) {
            toast.current?.show({
              severity: "success",
              summary: "No Issues! 🥳",
              detail:
                "Well done, you have no issues related to your account 🎉",
              life: 8000,
              content: customToastTemplate(),
            });
            setShowConfetti(true); // Show confetti when toast is displayed
            setTimeout(() => setShowConfetti(false), 8000); // Hide confetti after 3 seconds
          }
        }}
      >
            <div className="filter-item">
              <text style={{ color: "grey", fontSize: "small" }}>
                Your Issues
              </text>
            </div>
            <div>
              <InputSwitch
                checked={myIssuesOnly}
                onChange={handleMyIssuesToggle}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="grid-container" style={{ marginBottom: "1rem" }}>
          <div className="last-item">
            <div className="flex-column align-center justify-center">
              <div style={{ marginTop: "1rem" }}>
                {footerAction ?? (
                  <ExcelExport
                    data={filteredRows}
                    fileName={buildIssueExportName(
                      selectedFilters,
                      issueSelction,
                      exportName
                    )}
                    columnsToExport={exportColumns ?? ((item: any) => item)}
                    prepareExport={prepareExport}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast ref={toast} />
      {showConfetti && (
        <Confetti
          width={window.innerWidth} // Use window width
          height={window.innerHeight} // Use window height
          recycle={true} // Prevent confetti from recycling
          numberOfPieces={400} // Adjust the number of confetti pieces
        />
      )}
      
    </>
  );
};

export default AuditFilters;
